import React, { useState } from "react";
import { AccountCircle } from "@mui/icons-material";
import { useServices } from "../../services/Services";
import ContentLoader from "../ContentLoader/ContentLoader";
import CommunicationPreferencesItem from "../UserMenu/CommunicationPreferencesItem";
import ReportPreferencesItem from "../UserMenu/ReportPreferencesItem";
import StripeBillingItem from "../UserMenu/StripeBillingItem";
import FeedbackItem from "../UserMenu/FeedbackItem";
import UserMenuItem from "../UserMenu/UserMenuItem";
import { FormatAlignLeft, Logout } from "@mui/icons-material";
import { API_URL, auth } from "../../auth/client";
import { useDeepDiveTracking } from "../../context/DeepDiveTrackingContext";
import css from "./Sidebar.module.css";

const SidebarUserProfile: React.FC = () => {
  const services = useServices();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { clearDeepDiveTracking } = useDeepDiveTracking();

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Don't close menu if a dialog is currently open
      const hasOpenDialog = document.querySelector("dialog[open]");
      if (
        showUserMenu &&
        !hasOpenDialog &&
        !(event.target as Element).closest(".userProfile, .userMenuPopup")
      ) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  const { data: ontologyUser, isLoading: isUserLoading } =
    services.User.useGetCurrent();
  const userEmail = ontologyUser?.email ?? "";
  const { data: company, isLoading: isCompanyLoading } =
    services.User.useGetEmployer(ontologyUser ?? undefined);
  const { data: foundryUser, isLoading: isFoundryLoading } =
    services.User.useGetCurrentFoundryUser();
  const userFirstNameInitial =
    ontologyUser?.firstName?.charAt(0).toUpperCase() ?? "";
  const userLastNameInitial =
    ontologyUser?.lastName?.charAt(0).toUpperCase() ?? "";
  const userNameInitials = userFirstNameInitial + userLastNameInitial || "U";
  const profileImage =
    foundryUser?.attributes?.Picture?.toString() ||
    `https://cdn.auth0.com/avatars/${userNameInitials.toLowerCase()}.png`;

  const isLoading = isUserLoading || isCompanyLoading || isFoundryLoading;

  async function logOut() {
    clearDeepDiveTracking();
    await auth.signOut();
    window.location.href = API_URL + "/multipass/api/logout";
  }

  function closeUserMenu() {
    setShowUserMenu(false);
  }

  return (
    <>
      <div
        className={`${css.userProfile} userProfile`}
        onClick={() => setShowUserMenu(!showUserMenu)}
        title="User Menu"
      >
        <div className={css.profileImage}>
          {isLoading ? (
            <ContentLoader type="rounded-icon" />
          ) : (
            <img src={profileImage} alt={userNameInitials} />
          )}
        </div>
        <div className={css.profileLabel}>
          <span className={css.profileName}>
            {ontologyUser?.fullName ?? ""}
          </span>
          <span className={css.profileCompany}>{company?.name ?? ""}</span>
        </div>
      </div>

      {showUserMenu && (
        <div className={`${css.userMenuPopup} userMenuPopup`}>
          <div className={css.userMenuContent}>
            {userEmail && (
              <div className={css.userEmailHeader}>
                <div className={css.userEmailIcon}>
                  <AccountCircle className={css.personIcon} />
                </div>
                <div className={css.userEmail}>{userEmail}</div>
              </div>
            )}
            <ul className={css.userMenuItems}>
              <CommunicationPreferencesItem onClose={closeUserMenu} />
              <ReportPreferencesItem onClose={closeUserMenu} />
              <StripeBillingItem />
              <div className={css.menuDivider}></div>
              <FeedbackItem />
              <UserMenuItem
                Icon={FormatAlignLeft}
                to="https://unrulycorp.com/terms-and-conditions"
                target="_blank"
              >
                Terms & Conditions
              </UserMenuItem>
              <div className={css.menuDivider}></div>
              <UserMenuItem Icon={Logout} onClick={logOut}>
                Logout
              </UserMenuItem>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default SidebarUserProfile;
